<script setup lang="ts">
  import { ref, onMounted, type PropType, watchEffect } from 'vue'
  import AppContextMenu from './AppContextMenu.vue'
  import type { Image } from '@common/types/image'

  // 定义组件的 props
  const props = defineProps({
    src: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      required: false
    },
    title: {
      type: String,
      required: false
    },
    imageId: {
      type: String,
      required: false,
      default: ''
    },
    topicId: {
      type: String,
      required: false,
      default: ''
    },
    fullscreen: {
      type: String,
      required: false
    },
    allFullScreen: {
      type: Array as PropType<Image[]>,
      default: () => [],
      required: false
    },
    zoomOut: {
      type: Boolean,
      default: false
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    useBrandIcon: {
      type: Boolean,
      default: false
    },
    useSkeleton: {
      type: Boolean,
      default: true
    }
  })

  const emit = defineEmits(['onload'])

  // 定义响应式状态
  const loaded = ref(false)
  const error = ref(false)
  const imgSrc = ref('')
  const loadImage = () => {
    const img = new window.Image()
    img.src = props.src

    // 监听图像加载成功事件
    img.onload = () => {
      imgSrc.value = props.src
      loaded.value = true
      error.value = false
      const imgWidth = img.width
      const imgHeight = img.height
      // 不是放大状态回调
      !props.zoomOut && emit('onload', { width: imgWidth, height: imgHeight })
    }

    // 监听图像加载失败事件
    img.onerror = () => {
      loaded.value = false
      loaded.value = true
      error.value = true
    }
  }

  const imgShowGlobal = ref(false)
  const currentIndex = ref(0)
  const globalImg = () => {
    if (!props.fullscreen) return
    imgShowGlobal.value = true
    if (props.allFullScreen && props.allFullScreen.length > 0) {
      currentIndex.value = props.allFullScreen.findIndex(item => item.url === props.fullscreen)
    }
  }

  const prev = () => {
    if (!props.allFullScreen || props.allFullScreen.length <= 0) return
    currentIndex.value = currentIndex.value === 0 ? 0 : currentIndex.value - 1
  }

  const next = () => {
    if (!props.allFullScreen || props.allFullScreen.length <= 0) return
    currentIndex.value =
      currentIndex.value === props.allFullScreen.length - 1
        ? props.allFullScreen.length - 1
        : currentIndex.value + 1
  }
  // 在组件挂载后加载图像
  onMounted(() => {
    props.src && loadImage()
  })

  watchEffect(() => {
    props.src && loadImage()
  })
</script>

<template>
  <!-- 如果图像加载成功，则显示图像 -->
  <img
    v-show="!error"
    @click="globalImg"
    :class="['loaded-img', { pointer: props.fullscreen }, { 'global-zoom-out': props.zoomOut }]"
    v-if="loaded"
    :src="imgSrc"
    :alt="props.alt"
    :title="props.title"
  />

  <!-- 如果图像加载失败，则显示错误消息 -->
  <div class="loaded-error" v-if="error">
    <span class="hitems-img-failure"></span>
  </div>

  <!-- 如果图像正在加载，则显示加载提示 -->
  <div class="skeleton-item" :class="{ 'no-skeleton': !props.useSkeleton }" v-if="!loaded">
    <span
      v-if="showIcon"
      :class="[
        'skeleton-item-icon',
        useBrandIcon ? 'hitems-icon-placeholder' : 'hitems-skeleton-image'
      ]"
    ></span>
  </div>

  <!-- 全屏大图 -->
  <teleport to="body" v-if="imgShowGlobal">
    <div class="img-global-mask" @click.stop="imgShowGlobal = false">
      <a class="img-global-close hitems-close" @click="imgShowGlobal = false"></a>
      <AppContextMenu
        v-if="props.imageId || props.topicId"
        :topicId="props.topicId"
        :imageId="props.imageId || props.allFullScreen[currentIndex].uniqueImageId || ''"
      >
        <!-- 调用自己 -->
        <template v-if="props.allFullScreen && props.allFullScreen.length > 0">
          <template v-for="(item, index) in props.allFullScreen" :key="item.url">
            <AppImage :zoomOut="true" v-if="index === currentIndex" :src="item.url" />
          </template>
          <a
            :class="[
              'img-global-btn',
              'img-global-prev',
              'hitems-arrow-left',
              { disabled: currentIndex === 0 }
            ]"
            @click.stop="prev"
          ></a>
          <a
            :class="[
              'img-global-btn',
              'img-global-next',
              'hitems-arrow-left',
              { disabled: currentIndex === props.allFullScreen.length - 1 }
            ]"
            @click.stop="next"
          ></a>
        </template>
        <AppImage v-else :zoomOut="true" :src="props.fullscreen || ''" />
      </AppContextMenu>
      <AppImage v-else :zoomOut="true" :src="props.fullscreen || ''" />
    </div>
  </teleport>
</template>

<style scoped>
  div {
    font-style: italic;
    color: #888;
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    /* pointer-events: none; */
    -webkit-touch-callout: none;
    /*系统默认菜单被禁用*/
    -webkit-user-select: none;
    /*webkit浏览器*/
    -khtml-user-select: none;
    /*早期浏览器*/
    -moz-user-select: none;
    /*火狐*/
    -ms-user-select: none;
    /*IE10*/
    user-select: none;
  }

  img.pointer {
    cursor: pointer;
  }

  .loaded-error {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(240, 240, 240, 0.5);

    .hitems-img-failure {
      color: #d3d3d3;
      font-size: 4rem;
    }
  }

  .global-zoom-out {
    cursor: zoom-out;
  }

  .skeleton-item {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .skeleton-item-icon {
    color: #d3d3d3;
    font-size: 4rem;
    position: relative;
    z-index: 31;
  }

  .skeleton-item:before {
    content: '';
    position: absolute;
    z-index: 30;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(240, 240, 240, 1) 25%,
      rgba(240, 240, 240, 0.5) 37%,
      rgba(240, 240, 240, 1) 63%
    );
    background-size: 400% 100%;
    -webkit-animation: sheen 1.4s ease infinite;
    animation: sheen 1.4s ease infinite;
    border-radius: 2px;
  }

  .no-skeleton:before {
    background: rgba(240, 240, 240, 1);
  }

  @keyframes sheen {
    0% {
      background-position: 100% 50%;
    }

    100% {
      background-position: 0% 50%;
    }
  }

  .img-global-mask {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background:
      linear-gradient(0deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(20px);
    box-shadow: 0px 0px 52px 0px rgba(0, 0, 0, 0.25);
    z-index: 110;
  }

  .img-global-mask :deep(.loaded-img) {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1;
    object-fit: contain;
  }

  .img-global-close {
    position: absolute;
    top: 4rem;
    right: 4rem;
    width: 4rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aaa;
    background-color: #fff;
    border-radius: 50%;
    font-size: 2rem;
    z-index: 2;
  }

  .img-global-close:hover {
    color: #666;
  }

  .img-global-btn {
    position: absolute;
    width: 4rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aaa;
    background-color: #fff;
    border-radius: 50%;
    font-size: 2rem;
    z-index: 2;
    top: 50%;
    transform: translateY(-50%);
  }

  .img-global-btn:hover {
    color: #666;
  }

  .img-global-btn.disabled {
    background-color: #d2d3d6;
    color: #aaa;
    cursor: not-allowed;
  }

  .img-global-prev {
    left: 4rem;
  }

  .img-global-next {
    right: 4rem;
    transform: rotate(180deg);
  }
</style>
